import { Injectable } from '@angular/core';
import { ApiService } from '../api/api.service';
import {
  difference,
  find,
  forEach,
  intersectionWith,
  isEmpty,
  isEqual,
  differenceWith
} from 'lodash';
import { Subject, Observable, BehaviorSubject } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { DateFormatterService } from '../date.formatter/date.formatter.service';

const wcmatch = require('wildcard-match');

const MAX_FLOWS_TO_COMPARE = 10;

export interface FlowDetailsTags {
  tagName: string;
  tagValue: string;
}

interface FilterExpr {
  key: string;
  value: string;
}

enum SortMap {
  asc = 'ascending',
  desc = 'descending'
}

@Injectable({
  providedIn: 'root'
})
export class AnalyticsCommonService {
  flowStatusPath = '/flowdb/api/status';
  multipleflowPath = '/flowdb/api/flows/fetchflows';
  tagsPath = '/flowdb/api/flows/search/findOneByFlowName?name=';
  profilePath = '/flowdb/api/sla-profiles/search/findByName?name=';
  getAllLicensePath = '/licensing/licenses';
  public filterEvent: Subject<any> = new Subject(); //eslint-disable-line
  public fuzzyFilterApplied: Subject<any> = new Subject(); //eslint-disable-line
  compareFlows: any; //eslint-disable-line
  dataSource: any; //eslint-disable-line
  flowDetailResponse: any; //eslint-disable-line
  flowCount: number;
  tableSortedOrder: any; //eslint-disable-line
  flows: any; //eslint-disable-line
  filters: Array<[]>;
  logsConfigPath = '/en/assets/dashboard-config/analytics-dashboard-config.json';
  enrichmentOnlyModeStatusURL = '/configuration/analytics/rtilite/rti/getEnrichmentOnlyModeStatus';
  flowId: string;
  updateOverallScoreCard: BehaviorSubject<any>; //eslint-disable-line
  exportScorecardLoaded: BehaviorSubject<any>; //eslint-disable-line
  availabilityChartLoading = true;
  lossChartLoading = true;
  jitterChartLoading = true;
  latencyChartLoading = true;
  exportCSVReportPath = '/dataemitter/v1/data/export/csv?';
  constructor(
    private apiService: ApiService,
    private http: HttpClient,
    private dateFormatterService: DateFormatterService
  ) {
    this.compareFlows = [];
    this.flowCount = 0;
    this.tableSortedOrder = {
      tableview: { header: null, direction: null },
      issuesTable: { header: null, direction: null },
      flowDetailTable: { header: null, direction: null }
    };
    this.updateOverallScoreCard = new BehaviorSubject<number>(null);
    this.exportScorecardLoaded = new BehaviorSubject<number>(null);
  }

  getSessionData(key: string): any {
    //eslint-disable-line
    return sessionStorage.getItem(key);
  }
  setSessionData(key: string, val: any): any {
    //eslint-disable-line
    sessionStorage.setItem(key, JSON.stringify(val));
  }
  clearSessionData(key: string): any {
    //eslint-disable-line
    sessionStorage.removeItem(key);
  }
  getFilteredFlowNames(flows: any, tokens: any): any {
    //eslint-disable-line
    const allFilter = [];
    const groupedFilter = [];
    const filteredFlowNames = [];
    const falsyFilter = [];
    const exp = this.getExpressionFromTokens(tokens);
    const operators = this.getOperatorsFromTokens(tokens);
    // convert expression string to filter array
    forEach(exp, item => {
      item = item.replace(/~/g, '=');
      if (item.includes('!=')) {
        const key = item.split(' != ')[0];
        let val = item.split(' != ')[1];
        val = val.substring(1, val.length - 1);
        falsyFilter.push({ key: key, value: val });
        allFilter.push({ key: key, value: val });
      } else {
        const key = item.split(' = ')[0];
        let val = item.split(' = ')[1];
        val = val.substring(1, val.length - 1);
        allFilter.push({ key: key, value: val });
      }
    });

    // if there is an operator then there are more than one expression
    // grouping the filter expr for different operators
    // the grouping method followed here is, statements with AND in between are grouped together,
    // OR statements are considered as individual filter
    // Ex., (Province = Sofia AND Postcode = 1407) OR (Postcode = 1151)
    if (!isEmpty(operators)) {
      groupedFilter.push([allFilter.shift()]);
      for (const opr of operators) {
        if (opr == 'AND') {
          groupedFilter.push([...groupedFilter.pop(), allFilter.shift()]);
        } else if (opr == 'OR') {
          groupedFilter.push([allFilter.shift()]);
        }
      }
    }

    // compare the filter expression with each flow
    forEach(flows, flowData => {
      const flowFilters = [];
      const flowTags = [];
      flowFilters.push({ key: 'Flow', value: flowData.flow_name });
      if (flowData?.reflector_type) {
        flowFilters.push({ key: 'ReflectorType', value: flowData.reflector_type });
      }
      if (flowData.sla_profile) {
        flowFilters.push({ key: 'Profile', value: flowData.sla_profile });
      }
      if (flowData.tags) {
        flowFilters.push(...this.formTags(flowData.tags));
      }
      if (flowData.endpoints[0]) {
        flowFilters.push({
          key: 'Endpoint',
          value: flowData.endpoints[0].endpoint_name
        });
        if (flowData.endpoints[0].tags) {
          flowFilters.push(...this.formTags(flowData.endpoints[0].tags));
        }
      }
      if (flowData.endpoints[1]) {
        flowFilters.push({
          key: 'Endpoint',
          value: flowData.endpoints[1].endpoint_name
        });
        if (flowData.endpoints[1].tags) {
          flowFilters.push(...this.formTags(flowData.endpoints[1].tags));
        }
      }
      flowFilters.forEach(tag => {
        flowTags.push({ key: tag.key, value: '' });
      });

      if (!isEmpty(groupedFilter)) {
        const anyConditionsPassed = [];
        forEach(groupedFilter, filter => {
          if (filter.length == 1) {
            anyConditionsPassed.push(
              this.conditionToCheckOR(flowFilters, filter, falsyFilter, flowTags)
            );
          } else if (filter.length > 1) {
            anyConditionsPassed.push(
              this.conditionToCheckAND(flowFilters, filter, falsyFilter, flowTags)
            );
          }
        });

        if (anyConditionsPassed.includes(true)) {
          filteredFlowNames.push(flowData.flow_name);
        }
      } else {
        if (!isEmpty(falsyFilter)) {
          falsyFilter.forEach(exp => {
            exp.value = exp.value.trim();
          });
          if (falsyFilter[0].value.includes('*')) {
            const match = wcmatch(this.createWildcardmatchString(falsyFilter[0].value), {
              flags: 'i'
            });
            const data = flowFilters
              .filter(item => item.key.trim() === falsyFilter[0].key.trim())
              .map(val => val.value);
            if (data.filter(match).length === 0) {
              filteredFlowNames.push(flowData.flow_name);
            }
          } else if (falsyFilter[0].value.length === 0) {
            let found = false;
            flowTags.forEach(item => {
              if (item.key === falsyFilter[0].key) {
                found = true;
              }
              if (!found) {
                filteredFlowNames.push(flowData.flow_name);
              }
            });
          } else if (!flowFilters.some(tag => find(falsyFilter, tag))) {
            filteredFlowNames.push(flowData.flow_name);
          }
        } else {
          allFilter.forEach(exp => {
            exp.value = exp.value.trim();
          });
          if (allFilter[0].value.includes('*')) {
            const match = wcmatch(this.createWildcardmatchString(allFilter[0].value), {
              flags: 'i'
            });
            const data = flowFilters
              .filter(item => item.key.trim() === allFilter[0].key.trim())
              .map(val => val.value);
            if (data.some(match)) {
              filteredFlowNames.push(flowData.flow_name);
            }
          } else if (allFilter[0].value.length === 0) {
            let found = false;
            flowTags.forEach(item => {
              if (item.key === allFilter[0].key) {
                found = true;
              }
              if (found) {
                filteredFlowNames.push(flowData.flow_name);
              }
            });
          } else if (flowFilters.some(tag => find(allFilter, tag))) {
            filteredFlowNames.push(flowData.flow_name);
          }
        }
      }
    });
    return filteredFlowNames;
  }

  // eslint-disable-next-line
  conditionToCheckAND(flowFilters: any, filters: any, falsyFilter: any, flowTags: any): any {
    let fval = true,
      fwval = true,
      sval: boolean,
      wval: boolean;
    falsyFilter.forEach(exp => {
      exp.value = exp.value.trim();
    });
    filters.forEach(exp => {
      exp.value = exp.value.trim();
    });
    // pick the falsy expression for this filter
    const falsyExpr = intersectionWith(filters, falsyFilter, isEqual);
    if (falsyExpr.length) {
      const wildcardMatchFalseFilters = falsyExpr.filter(item => item.value.includes('*'));
      const stringMatchFalseFilters = falsyExpr.filter(
        item => item.value && !item.value.includes('*')
      );
      const onlyTagMatchingFalseFilters = falsyExpr.filter(item => !item.value);
      if (stringMatchFalseFilters.length > 0 && onlyTagMatchingFalseFilters.length > 0) {
        fval =
          !stringMatchFalseFilters.some(filter => find(flowFilters, filter)) &&
          !onlyTagMatchingFalseFilters.some(filter => find(flowTags, filter));
      } else if (stringMatchFalseFilters.length > 0) {
        fval = !stringMatchFalseFilters.some(filter => find(flowFilters, filter));
      } else if (onlyTagMatchingFalseFilters.length > 0) {
        fval = !onlyTagMatchingFalseFilters.some(filter => find(flowTags, filter));
      } else {
        fval = true;
      }
      if (difference(filters.filter(item => !item.value.includes('*'))).length > 0) {
        filters.forEach(exp => {
          exp.value = exp.value.trim();
        });
        const onlyTagMatchingFilters = this.getArrayDifference(
          filters.filter(item => !item.value),
          falsyFilter,
          ['key', 'value']
        );
        const filterWithValue = this.getArrayDifference(
          filters,
          filters.filter(item => !item.value),
          ['key', 'value']
        );
        if (
          onlyTagMatchingFilters.length > 0 &&
          difference(
            filterWithValue.filter(item => item.value && !item.value.includes('*')),
            stringMatchFalseFilters
          ).length > 0
        ) {
          sval =
            difference(
              filterWithValue.filter(item => item.value && !item.value.includes('*')),
              stringMatchFalseFilters
            ).every(filter => find(flowFilters, filter)) ||
            onlyTagMatchingFilters.every(filter => find(flowTags, filter));
        } else if (
          difference(
            filterWithValue.filter(item => item.value && !item.value.includes('*')),
            stringMatchFalseFilters
          ).length > 0
        ) {
          sval = differenceWith(
            filterWithValue.filter(item => item.value && !item.value.includes('*')),
            stringMatchFalseFilters,
            (a, b) => {
              return a.key === b.key && a.value === b.value;
            }
          ).every(filter => find(flowFilters, filter));
        } else if (onlyTagMatchingFilters.length > 0) {
          sval = onlyTagMatchingFilters.every(filter => find(flowTags, filter));
        }
      } else {
        sval = true;
      }
      if (wildcardMatchFalseFilters.length > 0) {
        fwval = !wildcardMatchFalseFilters.some(item =>
          flowFilters.find(el => {
            const match = wcmatch(this.createWildcardmatchString(item.value), {
              flags: 'i'
            });
            const value = el.value;
            return match(value);
          })
        );
      } else {
        fwval = true;
      }
      if (
        difference(
          filters.filter(item => item.value.includes('*')),
          wildcardMatchFalseFilters
        ).length > 0
      ) {
        wval = difference(
          filters.filter(item => item.value.includes('*')),
          wildcardMatchFalseFilters
        ).every(item =>
          flowFilters.find(el => {
            const match = wcmatch(this.createWildcardmatchString(item.value), {
              flags: 'i'
            });
            const value = el.value;
            return match(value);
          })
        );
      } else {
        wval = true;
      }
      return sval && fval && fwval && wval;
    }
    filters.forEach(exp => {
      exp.value = exp.value.trim();
    });
    const wildcardMatchFilters = filters.filter(item => item.value.includes('*'));
    const stringMatchFilters = filters.filter(item => item.value && !item.value.includes('*'));
    const onlyTagMatchingFilters = filters.filter(item => !item.value);
    if (stringMatchFilters.length > 0 || onlyTagMatchingFilters.length > 0) {
      let stringVal = true;
      let tagVal = true;
      if (stringMatchFilters.length > 0) {
        stringVal = stringMatchFilters.every(filter => find(flowFilters, filter));
      }
      if (onlyTagMatchingFilters.length > 0) {
        tagVal = onlyTagMatchingFilters.every(filter => find(flowTags, filter));
      }
      sval = stringVal && tagVal;
    } else {
      sval = true;
    }
    if (wildcardMatchFilters.length > 0) {
      wval = wildcardMatchFilters.every(item =>
        flowFilters.find(el => {
          const match = wcmatch(this.createWildcardmatchString(item.value), { flags: 'i' });
          const value = el.value;
          return match(value);
        })
      );
    } else {
      wval = true;
    }
    return sval && wval;
  }

  // eslint-disable-next-line
  conditionToCheckOR(flowFilters: any, filter: any, falsyFilter: any, flowTags: any): any {
    falsyFilter.forEach(exp => {
      exp.value = exp.value.trim();
    });
    filter.forEach(exp => {
      exp.value = exp.value.trim();
    });
    let fval = false,
      val = true;
    const falsyExpr = intersectionWith(filter, falsyFilter, isEqual);
    if (falsyExpr.length) {
      if (falsyExpr[0].value && falsyExpr[0].value.includes('*')) {
        const match = wcmatch(this.createWildcardmatchString(falsyExpr[0].value), {
          flags: 'i'
        });
        const data = flowFilters
          .filter(item => item.key.trim() === falsyExpr[0].key.trim())
          .map(val => val.value);
        fval = data.filter(match).length === 0;
      } else if (!falsyExpr[0].value) {
        fval = !flowTags.some(tag => find(filter, tag));
      } else {
        fval = !flowFilters.some(tag => find(falsyExpr, tag));
      }
      return fval; // return directly as anyway OR's are single statements
    }
    if (filter[0].value && filter[0].value.includes('*')) {
      const match = wcmatch(this.createWildcardmatchString(filter[0].value), {
        flags: 'i'
      });
      const data = flowFilters
        .filter(item => item.key.trim() === filter[0].key.trim())
        .map(val => val.value);
      val = data.some(match);
    } else if (!filter[0].value) {
      val = flowTags.some(tag => find(filter, tag));
    } else {
      val = flowFilters.some(tag => find(filter, tag));
    }
    return val;
  }
  getExpressionFromTokens(tokens: any): any {
    //eslint-disable-line
    const expressions = [];
    tokens.forEach(token => {
      expressions.push(token.keyword + ' ' + token.operator + ' ' + "'" + token.operands + "'");
    });
    return expressions;
  }

  // eslint-disable-next-line
  getOperatorsFromTokens(tokens: any): any {
    const operators = [];
    tokens.forEach(token => {
      if (token.logicalOp) {
        operators.push(token.logicalOp);
      }
    });
    return operators;
  }
  createWildcardmatchString(value: string): string {
    return value.replace('*:', '').trim();
  }
  getArrayDifference(array1: FilterExpr[], array2: FilterExpr[], props: string[]): any[] {
    //eslint-disable-line
    return array1
      .filter(function (o1) {
        return !array2.some(function (o2) {
          return o1.key === o2.key && o1.value === o2.value;
        });
      })
      .map(function (o) {
        return props.reduce(function (newo, name) {
          newo[name] = o[name];
          return newo;
        }, {});
      });
  }
  formTags(tagData: any): any {
    //eslint-disable-line
    const flowDetailsTags = [];
    forEach(tagData, item => {
      flowDetailsTags.push({ key: item.tag_name, value: item.tag_value });
    });
    return flowDetailsTags;
  }

  getSavedFilters$(): Observable<any> {
    //eslint-disable-line
    const url = '/tags/tagfilters';
    return this.apiService.get$<any>(url, {}); //eslint-disable-line
  }

  saveFilters$(body: Record<string, string>): Observable<any> {
    //eslint-disable-line
    const url = '/tags/tagfilters';
    return this.apiService.post$<any>(url, body); //eslint-disable-line
  }

  removeFilter$(id: number): Observable<any> {
    //eslint-disable-line
    return this.apiService.delete$<any>(`/tags/tagfilters/${id}`); //eslint-disable-line
  }
  getFlowStatus$(): Observable<any> {
    //eslint-disable-line
    const url = this.flowStatusPath;
    return this.apiService.get$<any>(url, {}); //eslint-disable-line
  }
  clear(): void {
    this.compareFlows = [];
  }

  getCompareFlowsLimit(): number {
    return MAX_FLOWS_TO_COMPARE;
  }
  getMultipleFlows$(flows: Array<[]>): Observable<any> {
    //eslint-disable-line
    return this.apiService.post$<any>(this.multipleflowPath, flows); //eslint-disable-line
  }

  getLogo(): Observable<any> {
    //eslint-disable-line
    const options = {
      headers: new HttpHeaders({
        Accept: 'text/plain, application/xhtml+xml, */*',
        'Content-Type': 'image/svg+xml'
      }),
      responseType: 'text' as 'text' //eslint-disable-line
    };
    return this.http.get(
      '/en/assets/persistent/configuration/analytics/reports/custom/logo.svg',
      options
    );
  }
  getMessage(): Observable<any> {
    //eslint-disable-line
    const options = {
      headers: new HttpHeaders({
        Accept: 'text/plain, application/xhtml+xml, */*',
        'Content-Type': 'text/plain; charset=utf-8'
      }),
      responseType: 'text' as 'text' //eslint-disable-line
    };
    return this.http.get(
      '/en/assets/persistent/configuration/analytics/reports/custom/message.txt',
      options
    );
  }

  getView(): boolean {
    const mapView = sessionStorage.getItem('mapView');
    return JSON.parse(mapView);
  }

  setFlowMutedFlag(flowId: string): void {
    const muted = find(this.flows, item => {
      return item.flow_name === flowId;
    });
    if (muted) {
      muted.ui_monitoring_status = 'mute';
    }
  }

  getSlaprofileName(flowName: string): any {
    //eslint-disable-line
    const profileName = this.flows.filter(item => {
      return item.flow_name === flowName;
    });

    if (profileName.length > 0) {
      return profileName[0].sla_profile;
    }
    return;
  }

  getFilters(): any {
    //eslint-disable-line
    const filters = sessionStorage.getItem('filters');
    return JSON.parse(filters);
  }

  setFilters(filters: any): void {
    //eslint-disable-line
    this.filters = filters;
    sessionStorage.setItem('filters', JSON.stringify(filters));
  }

  getTags$(flowName: any): Observable<any> {
    //eslint-disable-line
    const url = this.tagsPath + flowName;
    return this.apiService.get$<any>(url, {}); //eslint-disable-line
  }
  getSLAProfile$(profileName: any): Observable<any> {
    //eslint-disable-line
    const url = this.profilePath + profileName;
    return this.apiService.get$<any>(url, {}); //eslint-disable-line
  }

  formFlowTags(tagData): any {
    //eslint-disable-line
    const flowDetailsTags = [];
    forEach(tagData, item => {
      flowDetailsTags.push(this.createTags(item.tag_name, item.tag_value));
    });
    return flowDetailsTags;
  }

  createTags(tagName: string, tagValue: string): FlowDetailsTags {
    return {
      tagName: tagName,
      tagValue: tagValue
    };
  }

  //eslint-disable-next-line
  getLogsConfig$(): Observable<any> {
    //eslint-disable-next-line
    return this.apiService.get$<any>(this.logsConfigPath, {});
  }
  //eslint-disable-next-line
  getEnrichmentOnlyModeStatus$(): Observable<any> {
    //eslint-disable-next-line
    return this.apiService.get$<any>(this.enrichmentOnlyModeStatusURL, {});
  }

  getAllLicences(): any {
    //eslint-disable-line
    return this.apiService.get$(this.getAllLicensePath, {});
  }

  //eslint-disable-next-line
  saveReportTime(from: any, to: any, range: any): void {
    switch (range) {
      case 'LAST_1H':
      case 'LAST_1DAY':
      case 'LAST_1H_CACHED': {
        const fromTime = this.dateFormatterService.getDateTime(from);
        const toTime = this.dateFormatterService.getDateTime(to);
        const fromTimeUTC = this.dateFormatterService.getUTCDateTime(from);
        const toTimeUTC = this.dateFormatterService.getUTCDateTime(to);
        sessionStorage.setItem('dateRange', `${fromTime} - ${toTime}`);
        sessionStorage.setItem('dateRangeUTC', `${fromTimeUTC} - ${toTimeUTC}`);
        break;
      }
      case 'LAST_30DAYS':
      case 'CUSTOM': {
        const fromTime = this.dateFormatterService.getUTCDateTime(from);
        const toTime = this.dateFormatterService.getUTCDateTime(to);
        const fromTimeLocal = this.dateFormatterService.getDateTime(from);
        const toTimeLocal = this.dateFormatterService.getDateTime(to);
        sessionStorage.setItem('dateRange', `${fromTime} - ${toTime}`);
        sessionStorage.setItem('dateRangeLocal', `${fromTimeLocal} - ${toTimeLocal}`);
        break;
      }
    }
  }
  constructHavingFilter() {
    let viewMode = this.getSessionData('mapViewMode');
    viewMode = viewMode.replace(/"/g, '');
    if (viewMode === 'viewByScores') {
      return this.constructScoresHavingFilter();
    } else {
      return this.constructKPIsHavingFilter();
    }
  }
  addMoreOptionsInQuery(query): any {
    query.data.queries[0].having = this.constructHavingFilter();
    query.data.queries[0].bounds = this.getMapBounds();
    query.data.queries[0].zoom = this.getMapZoomLevel();
    return query;
  }
  getMapBounds() {
    const bounds = null; // to be done
    return bounds;
  }
  getMapZoomLevel() {
    const zoomLevel = null; // to be done
    return zoomLevel;
  }
  constructScoresHavingFilter() {
    let scoreVal = JSON.parse(this.getSessionData('scoreSliderVal'));
    if (!scoreVal) {
      scoreVal = { min: 0, max: 10 };
    }
    return [
      {
        op: '>=',
        logic: '',
        tag: 'Score',
        expression: scoreVal.min,
        pattern: false
      },
      {
        op: '<=',
        logic: 'AND',
        tag: 'Score',
        expression: scoreVal.max,
        pattern: false
      }
    ];
  }
  constructKPIsHavingFilter() {
    const filters = this.getFilters();
    let having = [];
    if (!filters) {
      return null;
    }
    if (
      filters &&
      filters.statusFiltersArray &&
      filters.statusFiltersArray.length === 3 &&
      filters.affectedByFiltersArray.length === 0
    ) {
      return null;
    } else if (
      filters &&
      filters.affectedByFiltersArray &&
      filters.affectedByFiltersArray.length > 0 &&
      (!filters.statusFiltersArray ||
        (filters.statusFiltersArray && filters.statusFiltersArray.length !== 3))
    ) {
      filters.affectedByFiltersArray.forEach(item => {
        if (filters.statusFiltersArray.indexOf('normal') === -1) {
          having.push({
            op: '!=',
            logic: having.length === 0 ? '' : 'AND',
            tag: this.capitalizeFirstLetter(item),
            expression: 'Normal',
            pattern: false
          });
        }
        if (filters.statusFiltersArray.indexOf('critical') === -1) {
          having.push({
            op: '!=',
            logic: having.length === 0 ? '' : 'AND',
            tag: this.capitalizeFirstLetter(item),
            expression: 'Critical',
            pattern: false
          });
        }
        if (filters.statusFiltersArray.indexOf('warning') === -1) {
          having.push({
            op: '!=',
            logic: having.length === 0 ? '' : 'AND',
            tag: this.capitalizeFirstLetter(item),
            expression: 'Warning',
            pattern: false
          });
        }
      });
    } else if (
      filters &&
      filters.statusFiltersArray?.length !== 3 &&
      (!filters.affectedByFiltersArray || filters.affectedByFiltersArray?.length === 0)
    ) {
      if (filters.statusFiltersArray.indexOf('normal') === -1) {
        having.push({
          op: '!=',
          logic: having.length === 0 ? '' : 'AND',
          tag: 'Violation',
          expression: 'Normal',
          pattern: false
        });
      }
      if (filters.statusFiltersArray.indexOf('critical') === -1) {
        having.push({
          op: '!=',
          logic: having.length === 0 ? '' : 'AND',
          tag: 'Violation',
          expression: 'Critical',
          pattern: false
        });
      }
      if (filters.statusFiltersArray.indexOf('warning') === -1) {
        having.push({
          op: '!=',
          logic: having.length === 0 ? '' : 'AND',
          tag: 'Violation',
          expression: 'Warning',
          pattern: false
        });
      }
    } else if (
      filters &&
      filters.statusFiltersArray?.length === 3 &&
      filters.affectedByFiltersArray?.length > 0
    ) {
      having.push({
        op: '!=',
        logic: having.length === 0 ? '' : 'AND',
        tag: this.capitalizeFirstLetter(filters.affectedByFiltersArray[0]),
        expression: '-2',
        pattern: false
      });
    }

    return having;
  }
  capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }
  changeSortby(query, defaultSort, table) {
    query.data.queries[0].orderby = !this.tableSortedOrder[table].header
      ? defaultSort
      : this.createSortOrder(table);
    return query;
  }
  createSortOrder(table) {
    return [
      {
        fieldName: this.tableSortedOrder[table].header,
        nullsSortOrder: null,
        orderType: SortMap[this.tableSortedOrder[table].direction]
      }
    ];
  }
  exportCSVReport(query, params) {
    const httpOptions = {
      responseType: 'blob' as 'json'
    };
    return this.http.post(`${this.exportCSVReportPath}${params}`, query, httpOptions);
  }
}
